import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaSearch, FaSpinner, FaGift, FaEdit, FaTrash, FaPercent, FaRupeeSign } from "react-icons/fa";
import AdminPageLoader from './AdminPageLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';
const token = localStorage.getItem('authToken');

function OffersPage() {
    const [savingOffer, setSavingOffer] = useState(false);
    const [offerData, setOfferData] = useState({ name: '', value: '', unit: '', minAmount: '' });
    const [offerList, setOfferList] = useState([]);
    const [filteredOffers, setFilteredOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const handleOfferList = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/offerdata`);
            const offers = response.data || [];
            setOfferList(offers);
            setFilteredOffers(offers);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    // Search offers
    const handleSearchOffer = (event) => {
        const search = event.target.value.toLowerCase();
        setSearchTerm(search);
        
        if (search) {
            const filtered = offerList.filter(offer => 
                offer.name?.toLowerCase().includes(search) ||
                offer.value?.toString().includes(search) ||
                offer.unit?.toLowerCase().includes(search)
            );
            setFilteredOffers(filtered);
        } else {
            setFilteredOffers(offerList);
        }
    };

    const validateFields = () => {
        if (!offerData.name.trim()) {
            alert('Offer name is required.');
            return false;
        }
        if (!offerData.value.trim() || isNaN(Number(offerData.value))) {
            alert('Offer value must be a valid number.');
            return false;
        }
        if (!offerData.unit.trim()) {
            alert('Offer unit is required.');
            return false;
        }
        if (!offerData.minAmount.trim() || isNaN(Number(offerData.minAmount))) {
            alert('Minimum amount must be a valid number.');
            return false;
        }
        return true;
    };

    const handleAddOffer = async () => {
        if (!validateFields()) return;

        try {
            setSavingOffer(true);
            const response = await axios.post(`${API_BASE_URL}/add-offer`, offerData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            setOfferData({ name: '', value: '', unit: '', minAmount: '' });
            handleOfferList();
            // Close modal
            const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
            if (closeButton) {
                closeButton.click();
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setSavingOffer(false);
        }
    };

    const handleChange = (event) => {
        setOfferData({
            ...offerData,
            [event.target.name]: event.target.value,
        });
    };

    const handleDeleteOffer = async (id) => {
        if (!window.confirm('Are you sure you want to delete this offer?')) return;

        try {
            const response = await axios.delete(`${API_BASE_URL}/delete-offer/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            handleOfferList();
        } catch (error) {
            handleApiError(error);
        }
    };

    const getUnitIcon = (unit) => {
        return unit === 'Percent' ? <FaPercent /> : <FaRupeeSign />;
    };

    const getUnitColor = (unit) => {
        return unit === 'Percent' ? 'text-success' : 'text-primary';
    };

    useEffect(() => {
        handleOfferList();
    }, []);

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">OFFER MANAGEMENT</h4>
                                <p className="text-muted p-0 m-0">Manage promotional offers and discounts</p>
                            </div>
                            <button 
                                type="button" 
                                className="btn rounded-0 fw-bold"
                                style={{ background: '#1a237e', color: '#ffff01', border: '1.5px solid #1a237e', fontWeight: 700 }}
                                data-bs-toggle="modal" 
                                data-bs-target="#exampleModal"
                            >
                                <FaPlus className="me-2" />
                                Add Offer
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row mt-0">
                    <div className="col-md-6">
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control rounded-0 ps-5"
                                placeholder="Search by offer name, value, or unit..."
                                value={searchTerm}
                                onChange={handleSearchOffer}
                            />
                            <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                        </div>
                    </div>
                    <div className="col-md-6 text-end">
                        <small className="text-muted">
                            Showing {filteredOffers.length} of {offerList.length} offers
                        </small>
                    </div>
                </div>

                {loading ? (
                    <AdminPageLoader message="Loading offers..." />
                ) : (
                    <div className="row mt-4">
                        <div className="col-12">
                            {filteredOffers.length > 0 ? (
                                <div className="card border-0 shadow-sm">
                                    {/* <div className="card-header bg-white border-0">
                                        <h5 className="mb-0">
                                            <FaGift className="text-primary me-2" />
                                            Offers ({filteredOffers.length})
                                        </h5>
                                    </div> */}
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="border-0">#</th>
                                                        <th className="border-0">Offer Name</th>
                                                        <th className="border-0">Value</th>
                                                        <th className="border-0">Unit</th>
                                                        <th className="border-0">Min Amount</th>
                                                        <th className="border-0">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredOffers.map((offer, index) => (
                                                        <tr key={offer._id}>
                                                            <td className="align-middle">
                                                                <span className="badge bg-secondary">{index + 1}</span>
                                                            </td>
                                                            <td className="align-middle">
                                                                <strong>{offer.name}</strong>
                                                            </td>
                                                            <td className="align-middle">
                                                                <strong className="text-success">
                                                                    {offer.value}
                                                                </strong>
                                                            </td>
                                                            <td className="align-middle">
                                                                <span className={`badge ${offer.unit === 'Percent' ? 'bg-success' : 'bg-primary'}`}>
                                                                    {getUnitIcon(offer.unit)} {offer.unit}
                                                                </span>
                                                            </td>
                                                            <td className="align-middle">
                                                                <span className="badge bg-info text-dark">{offer.minAmount || '-'}</span>
                                                            </td>
                                                            <td className="align-middle">
                                                                <div className="btn-group" role="group">
                                                                    <Link 
                                                                        to={`/admin/offers/edit/${offer._id}`} 
                                                                        className="btn btn-outline-primary btn-sm rounded-0"
                                                                        title="Edit Offer"
                                                                    >
                                                                        <FaEdit />
                                                                    </Link>
                                                                    <button
                                                                        className="btn btn-outline-danger btn-sm rounded-0"
                                                                        onClick={() => handleDeleteOffer(offer._id)}
                                                                        title="Delete Offer"
                                                                    >
                                                                        <FaTrash />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center py-5">
                                        <FaGift className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">
                                            {searchTerm ? 'No offers found matching your search.' : 'No offers found'}
                                        </h5>
                                        <p className="text-muted">
                                            {searchTerm ? 'Try adjusting your search terms.' : 'Add your first offer to get started.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Add Offer Modal */}
            <div className="modal fade adma" id="exampleModal" tabIndex={-1} aria-labelledby="exampleModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow">
                        <div className="modal-header border-0">
                            <h5 className="modal-title fw-bold">Add New Offer</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label fw-bold">Offer Name</label>
                                <input 
                                    type="text" 
                                    className="form-control rounded-0" 
                                    placeholder="Enter offer name" 
                                    name="name" 
                                    value={offerData.name} 
                                    onChange={handleChange} 
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-bold">Offer Value</label>
                                <input 
                                    type="number" 
                                    min={0} 
                                    className="form-control rounded-0" 
                                    placeholder="Enter offer value" 
                                    name="value" 
                                    value={offerData.value} 
                                    onChange={handleChange} 
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-bold">Offer Unit</label>
                                <select 
                                    className="form-select rounded-0" 
                                    name="unit" 
                                    value={offerData.unit} 
                                    onChange={handleChange}
                                >
                                    <option value="">Select unit</option>
                                    <option value="Percent">Percent (%)</option>
                                    <option value="Rupees">Rupees (₹)</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold">Minimum Amount</label>
                                <input 
                                    type="number" 
                                    min={0} 
                                    className="form-control rounded-0" 
                                    placeholder="Enter minimum order amount for this offer" 
                                    name="minAmount" 
                                    value={offerData.minAmount} 
                                    onChange={handleChange} 
                                />
                            </div>
                        </div>
                        <div className="modal-footer border-0">
                            <button type="button" className="btn rounded-0 fw-bold" style={{background:'#fff', color:'#1a237e', border:'1.5px solid #1a237e'}} data-bs-dismiss="modal">
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="btn rounded-0 fw-bold" 
                                style={{background:'#1a237e', color:'#ffff01', border:'1.5px solid #1a237e'}} 
                                onClick={handleAddOffer} 
                                disabled={savingOffer}
                            >
                                {savingOffer ? (
                                    <>
                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Offer'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                <div className='mt-5 mb-5 pb-5'></div>
            </div>
        </Fragment>
    );
}

export default OffersPage;
